import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Wallet as WalletIcon,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Calendar,
  Filter,
} from "lucide-react";
import { WALLET_API } from "../../data/api";

// Animated Balance Card
function BalanceCard({ balance }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-3xl p-8 text-white shadow-2xl overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="relative">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <WalletIcon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-white/80 text-sm">Available Balance</p>
              <p className="text-3xl font-bold">₹{balance.toFixed(2)}</p>
            </div>
          </div>
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ repeat: Infinity, duration: 3 }}
            className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center"
          >
            <Sparkles className="w-8 h-8" />
          </motion.div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-white/70 text-xs">Total Credits</p>
            <p className="text-xl font-bold">₹50,000</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-white/70 text-xs">Total Debits</p>
            <p className="text-xl font-bold">₹12,500</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3">
          <AddMoneyButton />
          <button className="flex-1 py-3 bg-white/20 backdrop-blur rounded-xl font-medium hover:bg-white/30 transition-colors flex items-center justify-center gap-2">
            <CreditCard size={18} />
            Withdraw
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// Add Money Button
function AddMoneyButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState("");

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(true)}
        className="flex-1 py-3 bg-white text-blue-600 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
      >
        <Plus size={20} />
        Add Money
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
                <h3 className="text-xl font-bold">Add Money to Wallet</h3>
                <p className="text-white/80 text-sm">Quick and secure payment</p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                  />
                </div>
                <div className="flex gap-2">
                  {[100, 500, 1000, 5000].map((val) => (
                    <button
                      key={val}
                      onClick={() => setAmount(val.toString())}
                      className="flex-1 py-2 bg-gray-100 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm font-medium"
                    >
                      ₹{val}
                    </button>
                  ))}
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/30"
                >
                  Proceed to Pay
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Transaction Item
function TransactionItem({ tx, index }) {
  const isCredit = tx.transactionType === "Credit";

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center justify-between p-4 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          isCredit ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
        }`}>
          {isCredit ? <ArrowDownRight size={24} /> : <ArrowUpRight size={24} />}
        </div>
        <div>
          <p className="font-semibold text-gray-800">
            {tx.note?.trim() || tx.transactionType}
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar size={14} />
            {new Date(tx.createdAt).toLocaleDateString()}
            <span className="text-gray-300">•</span>
            <Clock size={14} />
            {new Date(tx.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
      <div className="text-right">
        <p className={`text-xl font-bold ${isCredit ? "text-green-600" : "text-red-600"}`}>
          {isCredit ? "+" : "-"} ₹{tx.amount.toFixed(2)}
        </p>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          isCredit ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
        }`}>
          {isCredit ? <CheckCircle size={12} /> : <XCircle size={12} />}
          {tx.transactionType}
        </span>
      </div>
    </motion.div>
  );
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  const pages = [...Array(totalPages)].map((_, i) => i + 1);

  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <ChevronLeft size={20} />
      </motion.button>
      
      <div className="flex gap-1">
        {pages.map((page) => (
          <motion.button
            key={page}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onPageChange(page)}
            className={`w-10 h-10 rounded-xl font-medium transition-all ${
              currentPage === page
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                : "bg-white shadow-sm hover:bg-gray-50"
            }`}
          >
            {page}
          </motion.button>
        ))}
      </div>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <ChevronRight size={20} />
      </motion.button>
    </div>
  );
}

export default function Wallet() {
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.userId;

  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const totalPages = Math.ceil(transactions.length / pageSize);
  const paginatedData = transactions.slice((page - 1) * pageSize, page * pageSize);

  const fetchWallet = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const [balanceRes, txRes] = await Promise.all([
        axios.get(WALLET_API.GET_BALANCE(userId)),
        axios.get(WALLET_API.TRANSACTIONS(userId)),
      ]);
      setBalance(balanceRes.data.balance || 0);
      setTransactions(txRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, [userId]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <WalletIcon className="text-blue-600" />
            My Wallet
          </h1>
          <p className="text-gray-500 mt-1">Manage your wallet and transactions</p>
        </motion.div>

        {/* Balance Card */}
        <BalanceCard balance={balance} />

        {/* Transactions Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <TrendingUp className="text-blue-600" />
                Recent Transactions
              </h2>
              <p className="text-gray-500 text-sm mt-1">{transactions.length} transactions</p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm hover:shadow-md transition-all text-sm font-medium">
              <Filter size={16} />
              Filter
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1 }}
                className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"
              />
            </div>
          ) : paginatedData.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-3xl shadow-sm">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <WalletIcon className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Transactions Yet</h3>
              <p className="text-gray-500">Add money to your wallet to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedData.map((tx, index) => (
                <TransactionItem key={tx.transactionId} tx={tx} index={index} />
              ))}
              
              {totalPages > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </div>
          )}
        </motion.div>

        {/* Quick Tips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl border border-blue-100"
        >
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Sparkles className="text-blue-600" />
            Quick Tips
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-green-500 mt-0.5" />
              Add money to wallet for faster checkout
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-green-500 mt-0.5" />
              Keep sufficient balance for seamless transactions
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} className="text-green-500 mt-0.5" />
              Contact support for any wallet-related issues
            </li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}
